import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, Table2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/inventory`;
const POLL_MS = 2000;

// Zero → "-" (AC5/AC7), null → blank (no cost on record, AC8).
export const fmtAgingCell = (v, basis) => {
  if (v === null || v === undefined) return "";
  if (Math.abs(v) < 1e-9) return "-";
  return basis === "value"
    ? v.toLocaleString("en-IN", { maximumFractionDigits: 2 })
    : v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const errText = async (error, fallback) => {
  try {
    const d = error.response?.data;
    const parsed = d instanceof Blob ? JSON.parse(await d.text()) : d;
    if (parsed?.detail) return typeof parsed.detail === "string" ? parsed.detail : JSON.stringify(parsed.detail);
  } catch { /* keep fallback */ }
  return fallback;
};

const SummaryTable = ({ data, basis, kind }) => {
  const headers = [...data.dim_headers, ...data.labels, basis === "value" ? "Total Value" : "Total Qty", "% of Total"];
  const totalStart = headers.length - 2;
  return (
    <div className="overflow-auto max-h-[55vh] border border-zinc-200" data-testid={`${kind}-aging-summary-${basis}-table`}>
      <table className="min-w-max w-full text-xs border-collapse">
        <thead className="bg-zinc-900 text-white sticky top-0">
          <tr>
            {headers.map((h, i) => (
              <th
                key={h}
                className={`px-3 py-2 font-semibold whitespace-nowrap ${i >= totalStart ? "min-w-[96px] text-right" : "text-left"}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.key.join("|")} className="border-b border-zinc-100 hover:bg-zinc-50">
              {r.key.map((k, i) => <td key={i} className="px-3 py-1.5 whitespace-nowrap">{k}</td>)}
              {r.buckets.map((v, i) => <td key={i} className="px-3 py-1.5 text-right font-mono whitespace-nowrap">{fmtAgingCell(v, basis)}</td>)}
              <td className="px-3 py-1.5 text-right font-mono font-semibold whitespace-nowrap">{fmtAgingCell(r.total, basis)}</td>
              <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap">{r.total === null ? "-" : fmtAgingCell(r.pct)}</td>
            </tr>
          ))}
          <tr className="bg-zinc-100 font-bold" data-testid={`${kind}-aging-summary-${basis}-grand-total`}>
            <td className="px-3 py-1.5 whitespace-nowrap" colSpan={data.dim_headers.length}>Grand Total</td>
            {data.grand.map((v, i) => <td key={i} className="px-3 py-1.5 text-right font-mono whitespace-nowrap">{fmtAgingCell(v, basis)}</td>)}
            <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap">{fmtAgingCell(data.grand_total, basis)}</td>
            <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap">{data.grand_total ? "100" : "-"}</td>
          </tr>
          <tr className="bg-zinc-100 font-bold" data-testid={`${kind}-aging-summary-${basis}-pct-row`}>
            <td className="px-3 py-1.5 whitespace-nowrap" colSpan={data.dim_headers.length}>% of Total</td>
            {data.pct_row.map((v, i) => <td key={i} className="px-3 py-1.5 text-right font-mono whitespace-nowrap">{data.grand_total ? fmtAgingCell(v) : "-"}</td>)}
            <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap">{data.grand_total ? "100" : "-"}</td>
            <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap">-</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const SummaryView = ({ kind, params }) => {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    axios.get(`${API}/${kind}/aging-summary`, { params })
      .then((r) => setData(r.data))
      .catch(async (e) => { toast.error(await errText(e, "Failed to load aging summary")); setData({ error: true }); });
  }, [kind, params]);
  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/${kind}/aging-summary/download`, { params, responseType: "blob" });
      const name = r.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") || `${kind}_aging_summary.xlsx`;
      saveAs(new Blob([r.data]), name);
    } catch (e) { toast.error(await errText(e, "Failed to download aging summary")); }
    setDownloading(false);
  };
  if (!data) return <div className="py-10 text-center text-sm text-zinc-500" data-testid={`${kind}-aging-summary-loading`}><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Building summary…</div>;
  if (data.error) return <div className="py-6 text-sm text-red-600" data-testid={`${kind}-aging-summary-error`}>Summary could not be built.</div>;
  return (
    <Tabs defaultValue="value" data-testid={`${kind}-aging-summary-tabs`}>
      <div className="flex items-center justify-between mb-3">
        <TabsList>
          <TabsTrigger value="value" data-testid={`${kind}-aging-summary-value-tab`}>Value</TabsTrigger>
          <TabsTrigger value="volume" data-testid={`${kind}-aging-summary-volume-tab`}>Volume</TabsTrigger>
        </TabsList>
        <Button size="sm" onClick={download} disabled={downloading} data-testid={`${kind}-aging-summary-download-btn`}>
          {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Download Summary
        </Button>
      </div>
      <TabsContent value="value"><SummaryTable data={data.value} basis="value" kind={kind} /></TabsContent>
      <TabsContent value="volume"><SummaryTable data={data.volume} basis="volume" kind={kind} /></TabsContent>
    </Tabs>
  );
};

const detailStartRequests = new Map();

const startDetailJob = (kind, params) => {
  const key = `${kind}:${JSON.stringify(params)}`;
  if (!detailStartRequests.has(key)) {
    const payload = { branch: params.branch ?? null, branch_id: params.branch_id ?? null };
    detailStartRequests.set(
      key,
      axios.post(`${API}/${kind}/aging-detail-jobs`, payload, { params })
        .finally(() => detailStartRequests.delete(key)),
    );
  }
  return detailStartRequests.get(key);
};

const DetailJob = ({ kind, params, branchLabel }) => {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const poll = async (id) => {
      try {
        const r = await axios.get(`${API}/aging-detail-jobs/${id}`);
        if (cancelled) return;
        setJob(r.data);
        if (!["READY", "FAILED"].includes(r.data.status)) timer.current = setTimeout(() => poll(id), POLL_MS);
      } catch (e) { if (!cancelled) setJob({ status: "FAILED", error: await errText(e, "Lost track of the export job") }); }
    };
    startDetailJob(kind, params)
      .then((r) => { if (!cancelled) { setJob(r.data); poll(r.data.job_id); } })
      .catch(async (e) => { if (!cancelled) setJob({ status: "FAILED", error: await errText(e, "Could not start the export") }); });
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [kind, params]);
  const download = async () => {
    try {
      const jobId = job.id || job.job_id;
      const r = await axios.get(`${API}/aging-detail-jobs/${jobId}/download`, { responseType: "blob" });
      saveAs(new Blob([r.data]), job.filename || `${kind}_aging_detail.xlsx`);
    } catch (e) { toast.error(await errText(e, "Failed to download detail export")); }
  };
  const status = job?.status || "PENDING";
  const scopeLabel = job?.branch || branchLabel || "All branches";
  return (
    <div className="py-8 text-center" data-testid={`${kind}-aging-detail-status`} data-status={status}>
      <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3" data-testid={`${kind}-aging-detail-scope`}>Scope: {scopeLabel}</p>
      {status === "READY" ? (
        <>
          <p className="text-sm text-zinc-700 mb-1">Detail export ready — {job.row_count?.toLocaleString("en-IN")} rows, Value and Volume worksheets.</p>
          <Button className="mt-3" onClick={download} data-testid={`${kind}-aging-detail-download-btn`}><Download className="w-4 h-4 mr-2" />Download Detail</Button>
        </>
      ) : status === "FAILED" ? (
        <p className="text-sm text-red-600" data-testid={`${kind}-aging-detail-error`}>{job?.error || "Export failed"}</p>
      ) : (
        <p className="text-sm text-zinc-600"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Generating detail export… you can keep this open; the download appears when it's ready.</p>
      )}
    </div>
  );
};

/** Aging report panel — replaces the page's "Download Aging" dropdown.
 *  `kind` is implied by the host page; `params` is the page's existing
 *  branch filter ({branch} for RM, {branch_id} for FG). */
export const AgingReportPanel = ({ kind, params, branchLabel }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);
  const title = kind === "rm" ? "RM Aging Report" : "FG Aging Report";
  return (
    <>
      <Button variant="secondary" className="uppercase text-xs tracking-wide" onClick={() => { setMode(null); setOpen(true); }} data-testid={`${kind}-aging-report-btn`}>
        <FileSpreadsheet className="w-4 h-4 mr-2" strokeWidth={1.5} />Aging Report
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl" data-testid={`${kind}-aging-report-dialog`}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription data-testid={`${kind}-aging-report-branch`}>Branch: {branchLabel || "All branches"}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button variant={mode === "summary" ? "default" : "outline"} onClick={() => setMode("summary")} data-testid={`${kind}-aging-summary-btn`}>
              <Table2 className="w-4 h-4 mr-2" />Summary
            </Button>
            <Button variant={mode === "detail" ? "default" : "outline"} onClick={() => setMode("detail")} data-testid={`${kind}-aging-detail-btn`}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />Detail (generate file)
            </Button>
          </div>
          {mode === "summary" && <SummaryView kind={kind} params={params} />}
          {mode === "detail" && <DetailJob kind={kind} params={params} branchLabel={branchLabel} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AgingReportPanel;
